// src/components/child/OnStreetMapEditor.jsx
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ChevronRight, X, Save, Maximize2, Loader2, MapPinned,
  Plus, Copy, Trash2, Move, Pentagon, Pencil,
} from 'lucide-react';
import { apiCall } from '@/api/apiCall';
import { GOOGLE_MAP_KEY } from '@/constants/constant';
import { getDeviceColor, getDeviceTypeName } from '@/components/child/offStreetDeviceVisuals';
import ReactSelect from '@/components/common/ReactSelect';

/* ============================================================================
 * MAP STYLES — Labels toggle ke saath (OffStreetMapPage jaisa)
 * ==========================================================================*/
const getDarkMapStyles = (hideLabels = false) => [
  { elementType: 'geometry', stylers: [{ color: '#1a1d28' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1d28' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8b92a8' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6b7280' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c3142' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f1419' }] },
  ...(hideLabels ? [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }] : []),
];
const getLightMapStyles = (hideLabels = false) => [
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dbeafe' }] },
  ...(hideLabels ? [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }] : []),
];
// 👇 Hybrid (Satellite View) mein sirf labels hide karne ke liye — baaki satellite
// imagery waisi hi rehti hai. Google Maps 'hybrid' mapType par bhi styles apply hote hain.
const getHybridMapStyles = (hideLabels = false) =>
  hideLabels ? [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }] : [];

// STREET ka color OnStreetParkingPage jaisa hi indigo (#6366F1) — pehle green
// (#10B981) tha, ParkingPage mein street polygon isi indigo shade mein hai.
const LEVEL_COLORS = { CITY: '#3B82F6', STREET: '#6366F1', DEVICE: '#F59E0B' };
const POLYGON_CORNER_COUNT = 4;

/* ============================================================================
 * MARKER ICON PATHS
 * ==========================================================================*/
const MARKER_ICON_PATHS = {
  BNA: '<rect width="20" height="12" x="2" y="6" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/>',
  TX: '<rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>',
  DEFAULT: '<path d="M9 17V7h4a3 3 0 0 1 0 6H9"/>',
};
const getMarkerIconPaths = (deviceType) => {
  if (!deviceType) return MARKER_ICON_PATHS.DEFAULT;
  const t = deviceType.toUpperCase();
  if (t.includes('BNA')) return MARKER_ICON_PATHS.BNA;
  if (t.includes('TX')) return MARKER_ICON_PATHS.TX;
  return MARKER_ICON_PATHS.DEFAULT;
};
const getMarkerColor = (deviceType, status) =>
  String(status).toUpperCase() === 'INACTIVE' ? '#9CA3AF' : getDeviceColor(deviceType);

/* ============================================================================
 * SCRIPT LOADING
 * ==========================================================================*/
function loadScriptTag(id, src) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === 'true') { resolve(); return; }
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps script')));
      return;
    }
    const script = document.createElement('script');
    script.id = id; script.src = src; script.async = true; script.defer = true;
    script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
}
async function ensureGoogleMapsLoaded() {
  if (!window.google?.maps) {
    await loadScriptTag('google-maps-script-core', `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_KEY}&libraries=geometry&v=weekly`);
  }
  if (window.google?.maps && !window.google.maps.geometry && typeof window.google.maps.importLibrary === 'function') {
    try { await window.google.maps.importLibrary('geometry'); } catch (e) { /* non-critical */ }
  }
}

/* ============================================================================
 * MARKER ICON BUILDERS
 * ==========================================================================*/
function buildPinIcon(google, deviceType, color, { selected = false, faded = false, armed = false } = {}) {
  const size = armed ? 52 : selected ? 50 : 38;
  const opacity = faded ? '0.45' : selected || armed ? '1' : '0.85';
  const iconPaths = getMarkerIconPaths(deviceType);
  const ringColor = '#22c55e';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
    ${armed ? `<circle cx="32" cy="28" r="29" fill="none" stroke="${ringColor}" stroke-width="3" stroke-dasharray="6 5"><animateTransform attributeName="transform" type="rotate" from="0 32 28" to="360 32 28" dur="2.2s" repeatCount="indefinite"/></circle>` : ''}
    <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill="${color}" opacity="${opacity}"/>
    <circle cx="32" cy="25" r="11" fill="white" opacity="${opacity}"/>
    <g transform="translate(25,18) scale(0.583)" fill="none" stroke="${color}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" opacity="${opacity}">
      ${iconPaths}
    </g>
    ${armed ? `<circle cx="32" cy="25" r="12.5" fill="none" stroke="${ringColor}" stroke-width="2.5"/>` : ''}
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size),
  };
}
function buildLabelIcon(google, text, color) {
  const w = Math.max(60, text.length * 7 + 20);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="24" viewBox="0 0 ${w} 24">
    <rect x="0" y="0" width="${w}" height="24" rx="6" fill="${color}" opacity="0.94"/>
    <text x="${w / 2}" y="12" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${text}</text>
  </svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(w, 24), anchor: new google.maps.Point(w / 2, 12) };
}
function buildClusterMarkerIcon(google, count, color) {
  const size = 34;
  const label = count > 99 ? '99+' : String(count);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
    <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill="${color}" opacity="0.9"/>
    <circle cx="32" cy="25" r="13" fill="white"/>
    <text x="32" y="26" font-family="sans-serif" font-size="${label.length > 2 ? 11 : 15}" font-weight="800" fill="${color}" text-anchor="middle" dominant-baseline="central">${label}</text>
  </svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(size, size), anchor: new google.maps.Point(size / 2, size) };
}

/* ============================================================================
 * POLYGON HELPERS
 * ==========================================================================*/
function normalizePolygonCoords(coords) {
  if (!Array.isArray(coords) || !coords.length) return [];
  let result = coords;
  while (Array.isArray(result[0])) result = result.flat();
  return result;
}
function clearShadow(shadow) {
  if (!shadow) return;
  if (Array.isArray(shadow)) shadow.forEach((s) => s?.setMap(null));
  else shadow.setMap(null);
}
function setShadowPath(shadow, path) {
  if (!shadow) return;
  if (Array.isArray(shadow)) shadow.forEach((s) => s.setPath(path));
  else shadow.setPath(path);
}
// OnStreetParkingPage jaisa hi darken helper — stroke ko fill color se thora dark
// karne ke liye (border fill se distinct dikhta hai).
const darkenColor = (hex, amount = 0.35) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex;
  const h = hex.slice(1);
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (full.length !== 6) return hex;
  const num = parseInt(full, 16);
  let r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  r = Math.max(0, Math.round(r * (1 - amount)));
  g = Math.max(0, Math.round(g * (1 - amount)));
  b = Math.max(0, Math.round(b * (1 - amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};
function createThickPolygon(google, paths, color, map, options = {}) {
  const shadowLayers = [
    { weight: 20, opacity: 0.06 },
    { weight: 16, opacity: 0.1 },
    { weight: 12, opacity: 0.16 },
    { weight: 8, opacity: 0.24 },
    { weight: 5, opacity: 0.34 },
  ].map(({ weight, opacity }) =>
    new google.maps.Polygon({
      paths, strokeColor: '#000000', strokeWeight: weight, strokeOpacity: opacity,
      fillColor: color, fillOpacity: 0, clickable: false, zIndex: 0, map,
    })
  );
  const darkColor = darkenColor(color, 0.35);
  const polygon = new google.maps.Polygon({
    paths, strokeColor: darkColor, strokeWeight: 2.5, strokeOpacity: 0.95,
    fillColor: color, fillOpacity: 0.16, clickable: true, editable: false, zIndex: 1, map,
    ...options,
  });
  const path = polygon.getPath();
  ['insert_at', 'remove_at', 'set_at'].forEach((evt) => {
    google.maps.event.addListener(path, evt, () => {
      const arr = path.getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
      setShadowPath(shadowLayers, arr);
    });
  });
  return {
    polygon, shadow: shadowLayers,
    setMap: (m) => { polygon.setMap(m); shadowLayers.forEach((s) => s.setMap(m)); },
  };
}
const polygonCentroid = (coords) => ({
  lat: coords.reduce((s, p) => s + p.lat, 0) / coords.length,
  lng: coords.reduce((s, p) => s + p.lng, 0) / coords.length,
});

/* ============================================================================
 * DATA HELPERS
 * ==========================================================================*/
const collectDescendantDevices = (row, rowsIndex) => {
  const out = [];
  const stack = [row];
  while (stack.length) {
    const cur = stack.pop();
    if (cur.level === 'DEVICE' && cur.lat != null && cur.lng != null) out.push(cur);
    (rowsIndex.get(cur.id) || []).forEach((c) => stack.push(c));
  }
  return out;
};
const collectDirectChildDevices = (row, rowsIndex) =>
  (rowsIndex.get(row.id) || []).filter((r) => r.level === 'DEVICE' && r.lat != null && r.lng != null);
const collectSiblingDevices = (row, rowsIndex) =>
  (rowsIndex.get(row.parentId) || []).filter((r) => r.level === 'DEVICE' && r.id !== row.id && r.lat != null && r.lng != null);
const collectDirectChildren = (row, rowsIndex) => rowsIndex.get(row.id) || [];

const groupDevicesByCoord = (devices, precision = 6) => {
  const map = new Map();
  devices.forEach((d) => {
    const key = `${Number(d.lat).toFixed(precision)},${Number(d.lng).toFixed(precision)}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(d);
  });
  return Array.from(map.values());
};

const extractRowId = (r) => {
  if (!r) return '';
  return r.row_id || r.rowId || r.ROWID || r.ROW_ID || r.rowid || r.RowId || r.RowID || r.id_row || '';
};

/* ============================================================================
 * API CALLS
 * ==========================================================================*/
export async function saveOnStreetDeviceCoordinates(pndNumber, latitude, longitude, context = {}) {
  const res = await apiCall(
    'PUT',
    `/cp-map/pnd-street/${pndNumber}/coordinates`,
    { latitude, longitude, ...context },
    null
  );
  if (res?.success) return { latitude, longitude };
  throw new Error(res?.message || 'Failed to update coordinates');
}

async function duplicateOnStreetDeviceApi({ row_id, pnd_number, device_type }) {
  const res = await apiCall('POST', '/on-street/device/duplicate', { row_id, pnd_number, device_type }, null);
  if (res?.success) return res.fields?.data ?? res.data ?? { row_id, pnd_number, device_type };
  throw new Error(res?.message || 'Failed to duplicate device');
}

async function createOnStreetDeviceApi({ row_id, pnd_number, device_type, latitude, longitude }) {
  const res = await apiCall('POST', '/on-street/device/update', {
    row_id, pnd_number, device_type,
    latitude: String(latitude ?? ''),
    longitude: String(longitude ?? ''),
  }, null);
  if (res?.success) return res.fields?.data ?? res.data ?? {};
  throw new Error(res?.message || 'Failed to create device');
}

async function deleteOnStreetDeviceApi(row_id) {
  if (!row_id) throw new Error('row_id is required to delete device');
  const res = await apiCall('POST', '/on-street/device/delete', { row_id }, null);
  if (res?.success) return true;
  throw new Error(res?.message || 'Failed to delete device');
}

/* ── Polygon API (on-street) ─────────────────────────────────────────────
 * POST /cb/on-street-polygon/upsert
 * Body: { level_type, <identifier>, coordinates: [{lat,lng},...] }
 * ────────────────────────────────────────────────────────────────────── */
function buildOnStreetPolygonPayload(row, coordinates) {
  const payload = { level_type: row.level, coordinates, country_cd: '966' };

  if (row.level === 'CITY') {
    const raw = row.raw || {};
    payload.city_cd = raw.city_cd ?? row.code;
    if (raw.city_name || row.name) payload.city_name = raw.city_name ?? row.name;
  } else if (row.level === 'STREET') {
    const raw = row.raw || {};
    payload.street_cd = raw.street_cd ?? row.code;
    if (raw.street_name || row.name) payload.street_name = raw.street_name ?? row.name;

    // city context — pehle parentRaw try karo, warna street ke devices se city nikal lo
    const cityRaw = row.parentRaw || raw.devices?.[0] || {};
    if (cityRaw.city_cd) payload.city_cd = cityRaw.city_cd;
    if (cityRaw.city_name) payload.city_name = cityRaw.city_name;
  } else if (row.level === 'DEVICE') {
    const raw = row.raw || {};
    payload.pnd_number = raw.pnd_number ?? row.code ?? row.name;
  }
  return payload;
}

export async function saveOnStreetPolygon(row, coordinates) {
  const payload = buildOnStreetPolygonPayload(row, coordinates);
  const res = await apiCall('POST', '/on-street-polygon/upsert', payload, null);
  if (res?.success) return res.fields?.data ?? res.data ?? payload;
  throw new Error(res?.message || 'Failed to save polygon');
}

async function fetchOnStreetPolygonHierarchy(row) {
  const qs = new URLSearchParams();
  const raw = row.raw || {};
  if (raw.city_cd) qs.set('city_cd', raw.city_cd);
  if (raw.street_cd) qs.set('street_cd', raw.street_cd);
  if (raw.pnd_number) qs.set('pnd_number', raw.pnd_number);
  if (!qs.toString()) return [];
  const res = await apiCall('GET', `/on-street-polygon/get-all-under?${qs}`, null, null);
  if (res?.success) return res.data || [];
  throw new Error(res?.message || 'Failed to fetch polygon hierarchy');
}

/* ============================================================================
 * MAIN COMPONENT
 * ==========================================================================*/
export default function OnStreetMapEditor({
  row,
  rowsIndex,
  mode,
  polygon,
  onDeviceSave,
  onDeviceMoveSave,
  onAfterMutation,
  onJumpTo,
  onClose,
  breadcrumb = [],
  onBreadcrumbClick,
  isDarkMode = false,
}) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const activeMarkerRef = useRef(null);
  const refMarkersRef = useRef([]);
  const labelMarkersRef = useRef([]);
  const requestCloseRef = useRef(() => {});

  // 👇 Labels toggle ke liye refs (OffStreetMapPage jaisa)
  const labelsVisibleRef = useRef(true);
  const labelsHideTimerRef = useRef(null);
  const mapTypeToggleRef = useRef(null);
  const mapTypeRef = useRef('roadmap');
  const isDarkModeRef = useRef(isDarkMode);

  const activePolygonRef = useRef(null);
  const activeShadowRef = useRef(null);
  const childPolygonsRef = useRef(new Map());
  const activeChildPolygonRef = useRef(null);
  const childOriginalPathRef = useRef(null);
  const mainOriginalPathRef = useRef(null);
  const apiPolygonsMapRef = useRef(new Map());

  const deviceOverridesRef = useRef(new Map());
  const pulledOutIdsRef = useRef(new Set());
  const renderChildrenRef = useRef(() => {});

  const isDrawingRef = useRef(false);
  const drawPointsRef = useRef([]);
  const tempPolygonRef = useRef(null);
  const tempShadowRef = useRef(null);
  const tempMarkersRef = useRef([]);
  const drawTargetRef = useRef(row);

  const isDevice = row.level === 'DEVICE';
  const canAddDevice = row.level === 'STREET';
  const color = LEVEL_COLORS[row.level] || '#6366F1';

  const [ready, setReady] = useState(false);
  const [mapLoadError, setMapLoadError] = useState(null);
  const [markerPos, setMarkerPos] = useState(
    isDevice && row.lat != null && row.lng != null ? { lat: row.lat, lng: row.lng } : null
  );
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(null);

  const [hasPolygon, setHasPolygon] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawPointsCount, setDrawPointsCount] = useState(0);
  const [mainEditing, setMainEditing] = useState(false);
  const [childAction, setChildAction] = useState(null);
  const childActionRef = useRef(null);
  useEffect(() => { childActionRef.current = childAction; }, [childAction]);

  // Device popup + modals
  const [devicePopup, setDevicePopup] = useState(null);
  const [armedDeviceId, setArmedDeviceId] = useState(null);
  const armedDeviceIdRef = useRef(null);
  useEffect(() => { armedDeviceIdRef.current = armedDeviceId; }, [armedDeviceId]);

  const [duplicateModal, setDuplicateModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [addDeviceModal, setAddDeviceModal] = useState(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [isAddingDeviceType, setIsAddingDeviceType] = useState(false);
  const [newDeviceTypeInput, setNewDeviceTypeInput] = useState('');
  const [customDeviceTypes, setCustomDeviceTypes] = useState([]);
  const skipNextDeviceTypeBlurRef = useRef(false);

  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [hierarchyError, setHierarchyError] = useState(null);

  // 👇 mapType state — Default/Satellite toggle ke liye (OffStreetMapPage jaisa)
  const [mapType, setMapType] = useState('roadmap');

  // Refs sync with state (for use inside map init effect + Labels click handler)
  useEffect(() => { mapTypeRef.current = mapType; }, [mapType]);
  useEffect(() => { isDarkModeRef.current = isDarkMode; }, [isDarkMode]);

  // 👇 Labels visibility + mapType ke hisaab se styles apply karo.
  // Roadmap → dark/light theme styles (+ optional labels off)
  // Hybrid  → sirf labels off (agar labels hidden hain), warna empty
  const applyMapStyles = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const hideLabels = !labelsVisibleRef.current;
    const styles = mapTypeRef.current === 'roadmap'
      ? (isDarkModeRef.current ? getDarkMapStyles(hideLabels) : getLightMapStyles(hideLabels))
      : getHybridMapStyles(hideLabels);
    map.setOptions({ styles });
  }, []);

  const requestClose = useCallback((fn) => {
    if (dirty) { setConfirmDiscard(() => fn); return; }
    fn();
  }, [dirty]);
  requestCloseRef.current = requestClose;

  /* ── Init map ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureGoogleMapsLoaded();
        if (cancelled || !mapDivRef.current) return;
        const google = window.google;
        const center = markerPos || centerFromDescendants();
        const map = new google.maps.Map(mapDivRef.current, {
          center,
          zoom: isDevice ? 17 : 14,
          mapTypeId: mapTypeRef.current,
          styles: mapTypeRef.current === 'roadmap'
            ? (isDarkModeRef.current
                ? getDarkMapStyles(!labelsVisibleRef.current)
                : getLightMapStyles(!labelsVisibleRef.current))
            : getHybridMapStyles(!labelsVisibleRef.current),
          streetViewControl: false,
          mapTypeControl: false, // 👈 hum apna Default/Satellite toggle bana rahe hain
          fullscreenControl: true,
        });
        mapRef.current = map;

        // ── InfoWindow tight styling ──
        if (!document.getElementById('gm-infowindow-tight-style')) {
          const iwStyle = document.createElement('style');
          iwStyle.id = 'gm-infowindow-tight-style';
          iwStyle.textContent = `
            .gm-style-iw-c { padding: 10px 12px !important; }
            .gm-style-iw-d { overflow: visible !important; padding: 0 !important; }
            .gm-ui-hover-effect { top: 2px !important; right: 2px !important; }
          `;
          document.head.appendChild(iwStyle);
        }

        // ── View toggle + Labels animation styles ──
        if (!document.getElementById('gm-view-toggle-style')) {
          const styleTag = document.createElement('style');
          styleTag.id = 'gm-view-toggle-style';
          styleTag.textContent = `
            .gm-view-toggle-btn { transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease; }
            .gm-view-toggle-btn:hover:not(.gm-view-toggle-active) {
              background: ${isDarkModeRef.current ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.06)'} !important;
            }
            .gm-labels-popup {
              transition: opacity 0.25s ease, transform 0.25s ease;
            }
            .gm-labels-popup.gm-hidden {
              opacity: 0;
              transform: translateY(6px) scale(0.95);
              pointer-events: none;
            }
          `;
          document.head.appendChild(styleTag);
        }

        // ── Outer vertical stack ──
        const outerWrapper = document.createElement('div');
        outerWrapper.style.cssText = `
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          margin: 12px;
          font-family: 'Inter', Roboto, Arial, sans-serif; user-select: none;
        `;

        // ── Labels wrapper (initially HIDDEN) ──
        const labelsWrapper = document.createElement('div');
        labelsWrapper.className = 'gm-labels-popup gm-hidden';
        labelsWrapper.style.cssText = `
          display: flex; gap: 2px; padding: 3px;
          background: ${isDarkModeRef.current ? 'rgba(31,41,55,0.95)' : 'rgba(255,255,255,0.97)'};
          backdrop-filter: blur(6px);
          border: 1px solid ${isDarkModeRef.current ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'};
          border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
          font-size: 12px;
        `;

        // ── Default / Satellite wrapper ──
        const wrapper = document.createElement('div');
        wrapper.style.cssText = `
          display: flex; gap: 2px; padding: 3px;
          background: ${isDarkModeRef.current ? 'rgba(31,41,55,0.95)' : 'rgba(255,255,255,0.97)'};
          backdrop-filter: blur(6px);
          border: 1px solid ${isDarkModeRef.current ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'};
          border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
          font-size: 13px;
        `;

        // ── Auto-hide timer for Labels button ──
        let labelsHideTimer = null;
        const showLabelsBtnTemporarily = () => {
          labelsWrapper.classList.remove('gm-hidden');
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
          labelsHideTimer = setTimeout(() => {
            labelsWrapper.classList.add('gm-hidden');
          }, 2500);
        };
        labelsHideTimerRef.current = labelsHideTimer;

        labelsWrapper.addEventListener('mouseenter', () => {
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
        });
        labelsWrapper.addEventListener('mouseleave', () => {
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
          labelsHideTimer = setTimeout(() => {
            labelsWrapper.classList.add('gm-hidden');
          }, 1500);
        });

        // ── Default/Satellite button banao ──
        const makeBtn = (label, type) => {
          const btn = document.createElement('div');
          btn.textContent = label;
          btn.dataset.type = type;
          btn.className = 'gm-view-toggle-btn';
          btn.style.cssText = `
            padding: 7px 16px; cursor: pointer; white-space: nowrap; border-radius: 999px;
            font-weight: 500; letter-spacing: 0.01em;
            color: ${isDarkModeRef.current ? '#cbd5e1' : '#475569'};
          `;
          btn.addEventListener('click', () => {
            setMapType(type);
            // 👈 Default/Satellite click pe Labels button show karo
            showLabelsBtnTemporarily();
          });
          return btn;
        };

        const defaultBtn = makeBtn('Default View', 'roadmap');
        const satelliteBtn = makeBtn('Satellite View', 'hybrid');

        const updateActive = (activeType) => {
          [defaultBtn, satelliteBtn].forEach((btn) => {
            const isActive = btn.dataset.type === activeType;
            btn.classList.toggle('gm-view-toggle-active', isActive);
            btn.style.background = isActive ? 'var(--primary)' : 'transparent';
            btn.style.color = isActive
              ? 'var(--primary-foreground)'
              : isDarkModeRef.current
                ? '#cbd5e1'
                : '#475569';
            btn.style.fontWeight = isActive ? '600' : '500';
            btn.style.boxShadow = isActive ? '0 1px 4px rgba(73,127,253,0.4)' : 'none';
          });
        };
        updateActive(mapTypeRef.current);
        mapTypeToggleRef.current = updateActive;

        // ── Labels toggle button (initially hidden) ──
        const labelsBtn = document.createElement('div');
        labelsBtn.textContent = 'Labels';
        labelsBtn.className = 'gm-view-toggle-btn';
        labelsBtn.style.cssText = `
          padding: 5px 14px; cursor: pointer; white-space: nowrap; border-radius: 999px;
          font-weight: 500; letter-spacing: 0.01em;
          color: ${isDarkModeRef.current ? '#cbd5e1' : '#475569'};
        `;

        const updateLabelsBtnStyle = () => {
          const isOn = labelsVisibleRef.current;
          labelsBtn.classList.toggle('gm-view-toggle-active', isOn);
          labelsBtn.style.background = isOn ? 'var(--primary)' : 'transparent';
          labelsBtn.style.color = isOn
            ? 'var(--primary-foreground)'
            : isDarkModeRef.current
              ? '#cbd5e1'
              : '#475569';
          labelsBtn.style.fontWeight = isOn ? '600' : '500';
          labelsBtn.style.boxShadow = isOn ? '0 1px 4px rgba(73,127,253,0.4)' : 'none';
        };
        updateLabelsBtnStyle();

        labelsBtn.addEventListener('click', () => {
          labelsVisibleRef.current = !labelsVisibleRef.current;
          applyMapStyles();
          updateLabelsBtnStyle();
          showLabelsBtnTemporarily();
        });

        // ── Assemble ──
        labelsWrapper.appendChild(labelsBtn);
        wrapper.appendChild(defaultBtn);
        wrapper.appendChild(satelliteBtn);
        outerWrapper.appendChild(labelsWrapper);
        outerWrapper.appendChild(wrapper);

        map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(outerWrapper);

        setReady(true);
      } catch (err) {
        if (!cancelled) setMapLoadError(err?.message || 'Failed to load map');
      }
    })();
    return () => {
      cancelled = true;
      if (labelsHideTimerRef.current) clearTimeout(labelsHideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 👇 mapType ya isDarkMode change hone par styles aur toggle active state update karo
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setMapTypeId(mapType);
    applyMapStyles();
    mapTypeToggleRef.current?.(mapType);
  }, [mapType, isDarkMode, applyMapStyles]);

  function centerFromDescendants() {
    const pts = mode === 'view' ? collectDescendantDevices(row, rowsIndex) : collectSiblingDevices(row, rowsIndex);
    if (pts.length) return { lat: pts[0].lat, lng: pts[0].lng };
    return { lat: 24.7136, lng: 46.6753 };
  }

  /* ── Device CRUD handlers ────────────────────────────────────────────── */
  const handleMoveDevice = useCallback((device) => {
    pulledOutIdsRef.current.add(device.id);
    armedDeviceIdRef.current = device.id;
    setArmedDeviceId(device.id);
    setDevicePopup(null);
    renderChildrenRef.current();
  }, []);
  const cancelArmedMove = useCallback(() => {
    const id = armedDeviceIdRef.current;
    if (id != null) pulledOutIdsRef.current.delete(id);
    armedDeviceIdRef.current = null;
    setArmedDeviceId(null);
    renderChildrenRef.current();
  }, []);

  const openDuplicateModal = useCallback((device) => {
    setDevicePopup(null); setModalError(null);
    const raw = device.raw || {};
    setDuplicateModal({
      sourceDevice: device,
      row_id: raw.row_id ?? raw.rowId ?? raw.ROWID ?? device.rowId ?? device.id,
      pnd_number: '',
      device_type: device.deviceType ?? raw.device_type ?? '',
    });
  }, []);
  const openDeleteModal = useCallback((device) => {
    setDevicePopup(null); setModalError(null);
    const raw = device.raw || {};
    setDeleteConfirm({
      device,
      row_id: raw.row_id ?? raw.rowId ?? raw.ROWID ?? device.rowId ?? device.id,
    });
  }, []);
  const openAddDeviceModal = useCallback(() => {
    setModalError(null);
    const streetRowId =
      extractRowId(row.raw || {}) ||
      extractRowId(collectDirectChildDevices(row, rowsIndex)[0]?.raw || {}) || '';
    const existingDevice = collectDirectChildDevices(row, rowsIndex)[0];
    const streetCoord =
      existingDevice ? { lat: existingDevice.lat, lng: existingDevice.lng }
      : (row.lat != null && row.lng != null) ? { lat: row.lat, lng: row.lng }
      : null;
    setAddDeviceModal({
      row_id: streetRowId,
      pnd_number: '', device_type: '',
      latitude: streetCoord ? String(streetCoord.lat) : '',
      longitude: streetCoord ? String(streetCoord.lng) : '',
    });
  }, [row, rowsIndex]);

  const commitNewDeviceType = useCallback(() => {
    const newType = newDeviceTypeInput.trim();
    if (newType) {
      setCustomDeviceTypes((p) => (p.includes(newType) ? p : [...p, newType]));
      setAddDeviceModal((p) => (p ? { ...p, device_type: newType } : p));
    }
    setIsAddingDeviceType(false);
    setNewDeviceTypeInput('');
  }, [newDeviceTypeInput]);

  const deviceTypeOptions = useMemo(() => {
    const types = new Set();
    rowsIndex?.forEach?.((children) => {
      (children || []).forEach((r) => {
        if (r.level !== 'DEVICE') return;
        const t = r.deviceType ?? r.raw?.device_type;
        if (t) types.add(t);
      });
    });
    customDeviceTypes.forEach((t) => types.add(t));
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }, [rowsIndex, customDeviceTypes]);


  const submitDuplicate = async () => {
    if (!duplicateModal) return;
    if (!duplicateModal.pnd_number?.trim()) { setModalError('New PND Number is required.'); return; }
    if (!duplicateModal.device_type?.trim()) { setModalError('Device Type is required.'); return; }
    setModalSubmitting(true); setModalError(null);
    try {
      await duplicateOnStreetDeviceApi({
        row_id: duplicateModal.row_id,
        pnd_number: duplicateModal.pnd_number.trim(),
        device_type: duplicateModal.device_type.trim(),
      });
      setDuplicateModal(null);
      await onAfterMutation?.();
    } catch (err) { setModalError(err?.message || 'Failed to duplicate device'); }
    finally { setModalSubmitting(false); }
  };
  const submitDelete = async () => {
    if (!deleteConfirm) return;
    setModalSubmitting(true); setModalError(null);
    try {
      await deleteOnStreetDeviceApi(deleteConfirm.row_id);
      setDeleteConfirm(null);
      await onAfterMutation?.();
    } catch (err) { setModalError(err?.message || 'Failed to delete device'); }
    finally { setModalSubmitting(false); }
  };
  const submitCreate = async () => {
    if (!addDeviceModal) return;
    if (!addDeviceModal.pnd_number?.trim()) { setModalError('PND Number is required.'); return; }
    if (!addDeviceModal.device_type?.trim()) { setModalError('Device Type is required.'); return; }
    if (!addDeviceModal.row_id) { setModalError('row_id missing — backend se row_id nahi mila.'); return; }
    setModalSubmitting(true); setModalError(null);
    try {
      await createOnStreetDeviceApi({
        row_id: addDeviceModal.row_id,
        pnd_number: addDeviceModal.pnd_number.trim(),
        device_type: addDeviceModal.device_type.trim(),
        latitude: addDeviceModal.latitude,
        longitude: addDeviceModal.longitude,
      });
      setAddDeviceModal(null);
      await onAfterMutation?.();
    } catch (err) { setModalError(err?.message || 'Failed to create device'); }
    finally { setModalSubmitting(false); }
  };

  /* ── Draw Polygon handlers ───────────────────────────────────────────── */
  const cleanupTempDraw = useCallback(() => {
    tempMarkersRef.current.forEach((m) => m.setMap(null)); tempMarkersRef.current = [];
    if (tempPolygonRef.current) { tempPolygonRef.current.setMap(null); tempPolygonRef.current = null; }
    if (tempShadowRef.current) { clearShadow(tempShadowRef.current); tempShadowRef.current = null; }
    drawPointsRef.current = [];
    setDrawPointsCount(0);
  }, []);

  const startDraw = () => {
    if (!mapRef.current || isDrawingRef.current || childAction) return;
    if (mainEditing) cancelMainEdit();
    if (activePolygonRef.current) { activePolygonRef.current.setMap(null); activePolygonRef.current = null; setHasPolygon(false); }
    if (activeShadowRef.current) { clearShadow(activeShadowRef.current); activeShadowRef.current = null; }
    setDirty(true);
    cleanupTempDraw();
    drawTargetRef.current = row;
    isDrawingRef.current = true;
    setIsDrawing(true);
  };
  const cancelDraw = () => {
    cleanupTempDraw();
    isDrawingRef.current = false;
    setIsDrawing(false);
    drawTargetRef.current = row;
    if (childActionRef.current?.mode === 'draw') setChildAction(null);
  };
  const startChildDraw = (child) => {
    if (isDrawingRef.current || childAction) return;
    setDevicePopup(null);
    cleanupTempDraw();
    drawTargetRef.current = child;
    isDrawingRef.current = true;
    setIsDrawing(true);
    setChildAction({ child, mode: 'draw' });
  };

  const beginEditPolygon = (child, polygonInstance) => {
    if (isDrawingRef.current || childAction || !polygonInstance) return;
    setDevicePopup(null);
    childOriginalPathRef.current = polygonInstance.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    polygonInstance.setOptions({ editable: true, fillOpacity: 0.30, zIndex: 5 });
    activeChildPolygonRef.current = polygonInstance;
    setChildAction({ child, mode: 'edit' });
  };
  const startChildEdit = (child) => {
    const entry = childPolygonsRef.current.get(child.id);
    beginEditPolygon(child, entry?.polygon);
  };
  const startApiNodeEdit = (child) => {
    const entry = apiPolygonsMapRef.current.get(child.id);
    beginEditPolygon(child, entry?.polygon);
  };
  const cancelChildEdit = () => {
    if (activeChildPolygonRef.current && childOriginalPathRef.current) {
      activeChildPolygonRef.current.setPath(childOriginalPathRef.current);
      activeChildPolygonRef.current.setOptions({ editable: false, fillOpacity: 0.22, zIndex: 1 });
    }
    activeChildPolygonRef.current = null;
    childOriginalPathRef.current = null;
    setChildAction(null);
  };
  const saveChildEdit = async () => {
    if (!activeChildPolygonRef.current || !childActionRef.current) return;
    const child = childActionRef.current.child;
    const coordinates = activeChildPolygonRef.current.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    setChildAction({ child, mode: 'saving' });
    try {
      await saveOnStreetPolygon(child, coordinates);
      activeChildPolygonRef.current.setOptions({ editable: false, fillOpacity: 0.22, zIndex: 1 });
      activeChildPolygonRef.current = null;
      childOriginalPathRef.current = null;
      setChildAction(null);
      loadHierarchyPolygons();
      onAfterMutation?.();
    } catch (err) {
      alert(err?.message || 'Failed to save polygon');
      setChildAction({ child, mode: 'edit' });
    }
  };

  const startMainEdit = () => {
    if (!activePolygonRef.current || isDrawingRef.current || isDrawing || childAction) return;
    mainOriginalPathRef.current = activePolygonRef.current.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    activePolygonRef.current.setOptions({ editable: true, fillOpacity: 0.30, zIndex: 5 });
    setMainEditing(true);
    setDirty(true);
  };
  const cancelMainEdit = () => {
    if (activePolygonRef.current && mainOriginalPathRef.current) {
      activePolygonRef.current.setPath(mainOriginalPathRef.current);
    }
    activePolygonRef.current?.setOptions({ editable: false, fillOpacity: 0.16, zIndex: 1 });
    mainOriginalPathRef.current = null;
    setMainEditing(false);
    setDirty(false);
  };

  const finishDraw = useCallback(async () => {
    if (drawPointsRef.current.length < POLYGON_CORNER_COUNT) {
      alert(`Polygon banane ke liye ${POLYGON_CORNER_COUNT} points chahiye.`);
      return;
    }
    const google = window.google;
    const paths = drawPointsRef.current;
    const target = drawTargetRef.current;
    cleanupTempDraw();
    isDrawingRef.current = false;
    setIsDrawing(false);

    // Child target — save via API
    if (target && target.id !== row.id) {
      setChildAction({ child: target, mode: 'saving' });
      try {
        await saveOnStreetPolygon(target, paths);
        loadHierarchyPolygons();
        onAfterMutation?.();
      } catch (err) {
        alert(err?.message || 'Failed to save polygon');
      } finally {
        setChildAction(null);
        drawTargetRef.current = row;
      }
      return;
    }

    // Main polygon — draw + enter edit mode, save on "Save Changes"
    const { polygon: poly, shadow } = createThickPolygon(google, paths, color, mapRef.current, {
      editable: true, clickable: true,
    });
    activePolygonRef.current = poly;
    activeShadowRef.current = shadow;
    setHasPolygon(true); setDirty(true); setMainEditing(true);

    if (mapRef.current) {
      const bounds = new google.maps.LatLngBounds();
      paths.forEach((p) => bounds.extend(p));
      mapRef.current.fitBounds(bounds, 80);
    }
  }, [color, cleanupTempDraw, row, onAfterMutation]);

  const finishDrawRef = useRef(finishDraw);
  useEffect(() => { finishDrawRef.current = finishDraw; }, [finishDraw]);

  /* ── Load hierarchy polygons (children of current row) ───────────────── */
  const clearApiPolygons = useCallback(() => {
    apiPolygonsMapRef.current.forEach((entry) => {
      entry.polygon?.setMap(null);
      clearShadow(entry.shadow);
    });
    apiPolygonsMapRef.current = new Map();
  }, []);

  const loadHierarchyPolygons = useCallback(async () => {
    if (isDevice) return;
    clearApiPolygons();
    setHierarchyError(null);
    setHierarchyLoading(true);
    try {
      const tree = await fetchOnStreetPolygonHierarchy(row);
      const google = window.google;
      const map = mapRef.current;
      if (!google || !map) return;

      const walk = (nodes, depth) => {
        (nodes || []).forEach((node) => {
          const coords = normalizePolygonCoords(node.coordinates);
          if (coords.length) {
            const raw = row.raw || {};
            const isSelfNode =
              node.level_type === row.level &&
              (
                (row.level === 'CITY' && node.city_cd != null && String(node.city_cd) === String(raw.city_cd ?? row.code)) ||
                (row.level === 'STREET' && node.street_cd != null && String(node.street_cd) === String(raw.street_cd ?? row.code))
              );

            if (isSelfNode && !activePolygonRef.current) {
              const { polygon: poly, shadow } = createThickPolygon(google, coords, color, map, {
                editable: false, clickable: true, zIndex: 1,
              });
              activePolygonRef.current = poly;
              activeShadowRef.current = shadow;
              setHasPolygon(true);
            } else {
              const nodeColor = LEVEL_COLORS[node.level_type] || '#6366F1';
              const { polygon, shadow } = createThickPolygon(google, coords, nodeColor, map, {
                editable: false, clickable: true, zIndex: 1,
              });
              const rowId = `api-${node.level_type}-${node.id ?? node.city_cd ?? node.street_cd}`;
              apiPolygonsMapRef.current.set(rowId, { polygon, shadow, node });

              const childRow = {
                id: rowId,
                level: node.level_type,
                name: node.street_name || node.city_name || node.pnd_number || 'Polygon',
                code: node.street_cd || node.city_cd || node.pnd_number,
                raw: node,
              };
              polygon.addListener('click', () => {
                if (isDrawingRef.current || childActionRef.current) return;
                setDevicePopup({
                  lat: 0, lng: 0,
                  devices: [],
                  pos: null,
                  polygonNode: childRow,
                });
              });
            }
          }
          if (node.children?.length) walk(node.children, depth + 1);
        });
      };
      // eslint-disable-next-line no-console
      console.debug('[OnStreetMapEditor] get-all-under tree for row', row.name, '=', tree);
      walk(tree, 0);
    } catch (err) {
      setHierarchyError(err?.message || 'Failed to load polygon hierarchy');
    } finally {
      setHierarchyLoading(false);
    }
  }, [row, isDevice, clearApiPolygons]);

  /* ── Render children (devices + child polygons) ──────────────────────── */
  const renderChildren = useCallback((bounds) => {
    const google = window.google;
    const map = mapRef.current;
    if (!google || !map) return;

    refMarkersRef.current.forEach((m) => m.setMap(null)); refMarkersRef.current = [];
    labelMarkersRef.current.forEach((m) => m.setMap(null)); labelMarkersRef.current = [];
    childPolygonsRef.current.forEach((entry) => {
      entry.polygon?.setMap(null);
      clearShadow(entry.shadow);
    });
    childPolygonsRef.current = new Map();
    if (activeMarkerRef.current) { activeMarkerRef.current.setMap(null); activeMarkerRef.current = null; }

    /* ── coords mode ─────────────────────────────────────────────────── */
    if (mode === 'coords') {
      collectSiblingDevices(row, rowsIndex).forEach((sib) => {
        const marker = new google.maps.Marker({
          position: { lat: sib.lat, lng: sib.lng }, map,
          icon: buildPinIcon(google, sib.deviceType, getMarkerColor(sib.deviceType, sib.raw?.device_status), { faded: true }),
          title: sib.name, zIndex: 1,
        });
        marker.addListener('click', () => onJumpTo && requestClose(() => onJumpTo(sib, 'coords')));
        refMarkersRef.current.push(marker);
      });
      const pos = markerPos || centerFromDescendants();
      const marker = new google.maps.Marker({
        position: pos, map, draggable: true,
        icon: buildPinIcon(google, row.deviceType, getMarkerColor(row.deviceType, row.raw?.device_status), { selected: true }),
        zIndex: 10,
      });
      marker.addListener('dragend', (e) => { setMarkerPos({ lat: e.latLng.lat(), lng: e.latLng.lng() }); setDirty(true); });
      map.addListener('click', (e) => {
        marker.setPosition(e.latLng);
        setMarkerPos({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        setDirty(true);
      });
      activeMarkerRef.current = marker;
      return;
    }

    /* ── view mode — devices ─────────────────────────────────────────── */
    const allDevices = collectDirectChildDevices(row, rowsIndex);
    const getEffectiveCoord = (device) => {
      const override = deviceOverridesRef.current.get(device.id);
      return override ? { lat: override.lat, lng: override.lng } : { lat: device.lat, lng: device.lng };
    };
    const attachDragHandler = (marker, device) => {
      marker.addListener('dragend', (e) => {
        deviceOverridesRef.current.set(device.id, { device, lat: e.latLng.lat(), lng: e.latLng.lng() });
        pulledOutIdsRef.current.delete(device.id);
        armedDeviceIdRef.current = null; setArmedDeviceId(null);
        setDirty(true); renderChildrenRef.current();
      });
    };
    const openDevicePopupAt = (devices, e, lat, lng) => {
      e?.domEvent?.preventDefault?.();
      const rect = mapDivRef.current?.getBoundingClientRect();
      const clientX = e?.domEvent?.clientX, clientY = e?.domEvent?.clientY;
      const pos = (clientX != null && clientY != null && rect) ? { x: clientX - rect.left, y: clientY - rect.top } : null;
      setDevicePopup({ lat, lng, devices, pos });
    };
    const attachPopupHandlers = (marker, devices, lat, lng) => {
      marker.addListener('click', (e) => openDevicePopupAt(devices, e, lat, lng));
    };

    const pulledOut = allDevices.filter((d) => pulledOutIdsRef.current.has(d.id));
    const pulledOutIds = new Set(pulledOut.map((d) => d.id));
    const grouped = allDevices.filter((d) => !pulledOutIds.has(d.id));

    pulledOut.forEach((device) => {
      const { lat, lng } = getEffectiveCoord(device);
      const isArmed = armedDeviceIdRef.current === device.id;
      const icon = buildPinIcon(google, device.deviceType, getMarkerColor(device.deviceType, device.raw?.device_status), { selected: true, armed: isArmed });
      const marker = new google.maps.Marker({
        position: { lat, lng }, map, icon, draggable: isArmed, zIndex: isArmed ? 999 : 10,
        animation: isArmed ? google.maps.Animation.BOUNCE : null,
        title: `${device.name} (${getDeviceTypeName(device.deviceType)})`,
      });
      attachDragHandler(marker, device);
      attachPopupHandlers(marker, [device], lat, lng);
      refMarkersRef.current.push(marker);
      bounds?.extend({ lat, lng });
    });

    const deviceGroups = groupDevicesByCoord(grouped.map((d) => ({ ...d, ...getEffectiveCoord(d) })));
    deviceGroups.forEach((group) => {
      const { lat, lng } = group[0];
      if (group.length === 1) {
        const device = group[0];
        const isArmed = armedDeviceIdRef.current === device.id;
        const icon = buildPinIcon(google, device.deviceType, getMarkerColor(device.deviceType, device.raw?.device_status), { armed: isArmed });
        const marker = new google.maps.Marker({
          position: { lat, lng }, map, icon, draggable: isArmed, zIndex: isArmed ? 999 : undefined,
          animation: isArmed ? google.maps.Animation.BOUNCE : null,
          title: `${device.name} (${getDeviceTypeName(device.deviceType)})`,
        });
        attachDragHandler(marker, device);
        attachPopupHandlers(marker, [device], lat, lng);
        refMarkersRef.current.push(marker);
      } else {
        const icon = buildClusterMarkerIcon(google, group.length, getDeviceColor(group[0].deviceType));
        const marker = new google.maps.Marker({ position: { lat, lng }, map, icon, title: `${group.length} devices` });
        attachPopupHandlers(marker, group, lat, lng);
        refMarkersRef.current.push(marker);
      }
      bounds?.extend({ lat, lng });
    });

    /* ── child polygons (rows that have polygon coordinates) ─────────── */
    const directChildren = collectDirectChildren(row, rowsIndex);
    directChildren.forEach((child) => {
      const coords = normalizePolygonCoords(child.raw?.polygon || child.raw?.coordinates);
      if (!coords.length) return;
      const childColor = LEVEL_COLORS[child.level] || '#6366F1';
      const { polygon, shadow } = createThickPolygon(google, coords, childColor, map, {
        editable: false, clickable: true, zIndex: 1,
      });
      childPolygonsRef.current.set(child.id, { polygon, shadow, child });
      polygon.addListener('click', () => {
        if (isDrawingRef.current || childActionRef.current) return;
        setDevicePopup({
          lat: 0, lng: 0, devices: [], pos: null,
          polygonNode: child,
        });
      });
      coords.forEach((p) => bounds?.extend(p));
    });
  }, [mode, row, rowsIndex, markerPos, onJumpTo, requestClose]);

  useEffect(() => { renderChildrenRef.current = renderChildren; }, [renderChildren]);

  /* ── Map click handler for drawing ───────────────────────────────────── */
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const google = window.google;
    const map = mapRef.current;

    const clickListener = map.addListener('click', (e) => {
      setDevicePopup(null);
      if (armedDeviceIdRef.current != null) { armedDeviceIdRef.current = null; setArmedDeviceId(null); }
      if (!isDrawingRef.current) return;
      if (drawPointsRef.current.length >= POLYGON_CORNER_COUNT) return;
      const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      drawPointsRef.current = [...drawPointsRef.current, pos];
      setDrawPointsCount(drawPointsRef.current.length);
      const dot = new google.maps.Marker({
        position: pos, map,
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1.5 },
      });
      tempMarkersRef.current.push(dot);
      if (!tempPolygonRef.current) {
        const { polygon: tmpPoly, shadow: tmpShadow } = createThickPolygon(google, drawPointsRef.current, color, map, {
          clickable: false, editable: false,
        });
        tempPolygonRef.current = tmpPoly;
        tempShadowRef.current = tmpShadow;
      } else {
        tempPolygonRef.current.setPath(drawPointsRef.current);
        setShadowPath(tempShadowRef.current, drawPointsRef.current);
      }
      if (drawPointsRef.current.length === POLYGON_CORNER_COUNT) finishDrawRef.current?.();
    });

    return () => { google.maps.event.removeListener(clickListener); };
  }, [ready, color]);

  /* ── Mount render ────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    renderChildrenRef.current();
    if (!isDevice) {
      const raw = row.raw || {};
      const rawCoordSource =
        polygon ??
        raw.polygon ?? raw.coordinates ?? raw.polygon_coordinates ?? raw.coords ??
        raw.geo_polygon ?? raw.boundary ?? raw.boundary_coordinates ?? raw.geofence ??
        raw.geo_fence ?? raw.polygon_coords ?? raw.poly_coordinates ?? row.polygon ?? row.coordinates;
      const initialCoords = normalizePolygonCoords(rawCoordSource);
      if (!initialCoords.length) {
        // eslint-disable-next-line no-console
        console.debug('[OnStreetMapEditor] No polygon coords found — checked `polygon` prop and row.raw keys: polygon, coordinates, polygon_coordinates, coords, geo_polygon, boundary, boundary_coordinates, geofence, geo_fence, polygon_coords, poly_coordinates. polygon prop =', polygon, 'row.raw =', raw);
      }
      if (initialCoords.length) {
        const google = window.google;
        const { polygon: poly, shadow } = createThickPolygon(google, initialCoords, color, mapRef.current, {
          editable: false, clickable: true, zIndex: 1,
        });
        activePolygonRef.current = poly;
        activeShadowRef.current = shadow;
        setHasPolygon(true);
      }
      loadHierarchyPolygons();
    }
    fitToBounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, mode, row.id]);

  useEffect(() => {
    if (!ready || !mapRef.current || mode !== 'view') return;
    renderChildrenRef.current();
  }, [armedDeviceId, ready, mode]);

  const fitToBounds = useCallback(() => {
    if (!mapRef.current || !window.google) return;
    const google = window.google;
    const bounds = new google.maps.LatLngBounds();
    let any = false;
    if (activePolygonRef.current) { activePolygonRef.current.getPath().forEach((ll) => { bounds.extend(ll); any = true; }); }
    refMarkersRef.current.forEach((m) => { const p = m.getPosition(); if (p) { bounds.extend(p); any = true; } });
    childPolygonsRef.current.forEach((e) => e.polygon?.getPath().forEach((ll) => { bounds.extend(ll); any = true; }));
    apiPolygonsMapRef.current.forEach((e) => e.polygon?.getPath().forEach((ll) => { bounds.extend(ll); any = true; }));
    if (!any) return;
    mapRef.current.fitBounds(bounds, 60);
    if (mapRef.current.getZoom() > 18) mapRef.current.setZoom(18);
  }, []);

  /* ── Save ─────────────────────────────────────────────────────────────── */
  const handleSaveClick = async () => {
    if (mode === 'coords') {
      if (!markerPos || !onDeviceSave) return;
      setSaving(true); setSaveError(null);
      try { await onDeviceSave(row, markerPos); setDirty(false); }
      catch (err) { setSaveError(err?.message || 'Failed to save coordinates'); }
      finally { setSaving(false); }
      return;
    }

    const hasPolygonChange = hasPolygon && !!activePolygonRef.current && (mainEditing || isDrawing);
    const deviceMoves = Array.from(deviceOverridesRef.current.values());

    if (!hasPolygonChange && !deviceMoves.length) return;

    setSaving(true); setSaveError(null);
    try {
      if (hasPolygonChange) {
        const coordinates = activePolygonRef.current.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
        await saveOnStreetPolygon(row, coordinates);
        activePolygonRef.current?.setOptions({ editable: false, fillOpacity: 0.16, zIndex: 1 });
        mainOriginalPathRef.current = null;
        setMainEditing(false);
      }
      if (deviceMoves.length && onDeviceMoveSave) {
        for (const { device, lat, lng } of deviceMoves) {
          // eslint-disable-next-line no-await-in-loop
          await onDeviceMoveSave(device, { lat, lng });
        }
        deviceOverridesRef.current = new Map();
        pulledOutIdsRef.current = new Set();
        armedDeviceIdRef.current = null; setArmedDeviceId(null);
        renderChildren();
      }
      setDirty(false);
      await onAfterMutation?.();
    } catch (err) {
      setSaveError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const hasDeviceMoves = mode === 'view' && deviceOverridesRef.current.size > 0;
  const canSave = mode === 'coords'
    ? !!markerPos
    : (hasDeviceMoves || (hasPolygon && mainEditing));

  const iconBtn = (active) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
      active ? 'bg-primary/10 border-primary/40 text-primary' : 'border-border text-foreground hover:bg-secondary'
    }`;

  const childDrawTargetName = isDrawing && drawTargetRef.current && drawTargetRef.current.id !== row.id
    ? drawTargetRef.current.name : null;

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="fixed inset-0 z-[200] bg-background flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card flex-wrap gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground flex-wrap">
            {breadcrumb.map((c, i) => (
              <span key={c.id} className="flex items-center gap-1">
                <button className="hover:text-primary hover:underline font-medium" onClick={() => requestClose(() => onBreadcrumbClick?.(c))}>
                  {c.name}
                </button>
                {i < breadcrumb.length - 1 && <ChevronRight className="w-3 h-3" />}
              </span>
            ))}
          </div>
          <h2 className="text-sm font-bold text-foreground truncate">
            {isDevice ? 'Update Coordinates' : `Manage ${row.level} — Polygon & Devices`} — {row.name}
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Polygon controls */}
          {!isDevice && (
            <>
              {!hasPolygon ? (
                !isDrawing ? (
                  <button className={iconBtn(false)} onClick={startDraw} disabled={!!childAction}>
                    <Pentagon className="w-3.5 h-3.5" /> Draw Polygon
                  </button>
                ) : (
                  <button className={iconBtn(true)} onClick={cancelDraw}>
                    <X className="w-3.5 h-3.5" /> Cancel Draw{childDrawTargetName ? ` — ${childDrawTargetName}` : ''} ({drawPointsCount}/{POLYGON_CORNER_COUNT})
                  </button>
                )
              ) : (
                !isDrawing && !childAction && (
                  !mainEditing ? (
                    <button className={iconBtn(false)} onClick={startMainEdit}>
                      <Pencil className="w-3.5 h-3.5" /> Edit Polygon
                    </button>
                  ) : (
                    <button className={iconBtn(true)} onClick={cancelMainEdit}>
                      <X className="w-3.5 h-3.5" /> Cancel Edit
                    </button>
                  )
                )
              )}
              <div className="w-px h-5 bg-border mx-1" />
            </>
          )}
          <button className={iconBtn(false)} onClick={fitToBounds}>
            <Maximize2 className="w-3.5 h-3.5" /> Fit to Bounds
          </button>
          {!isDevice && canAddDevice && (
            <button className={iconBtn(false)} onClick={openAddDeviceModal}>
              <Plus className="w-3.5 h-3.5" /> Add Device
            </button>
          )}
          <button className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-muted-foreground hover:bg-secondary" onClick={() => requestClose(onClose)}>
            Cancel
          </button>
          {(isDevice || hasDeviceMoves || (hasPolygon && mainEditing)) && (
            <button
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
              onClick={handleSaveClick}
              disabled={!dirty || saving || !canSave || !!childAction}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? 'Saving...' : isDevice ? 'Save Coordinates' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {saveError && <div className="px-4 py-1.5 bg-red-500/10 border-b border-red-500/20 text-red-500 text-xs">{saveError}</div>}
      {hierarchyError && (
        <div className="px-4 py-1.5 bg-red-500/10 border-b border-red-500/20 text-red-500 text-xs flex items-center justify-between gap-2">
          <span>Couldn't load hierarchy: {hierarchyError}</span>
          <button className="underline font-semibold shrink-0" onClick={loadHierarchyPolygons}>Retry</button>
        </div>
      )}

      {/* Map */}
      <div className="flex-1 relative">
        <div ref={mapDivRef} className="w-full h-full" />
        {!ready && <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-sm text-muted-foreground">Loading map…</div>}
        {ready && mapLoadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/90 p-6 text-center">
            <div className="max-w-sm">
              <p className="text-sm font-semibold text-red-500 mb-1">Map failed to load</p>
              <p className="text-xs text-muted-foreground">{mapLoadError}</p>
            </div>
          </div>
        )}
        {ready && !mapLoadError && isDevice && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-primary/10 border border-primary/30 text-primary rounded-lg px-3 py-1.5 text-xs shadow">
            Drag the pin, or click anywhere on the map, to reposition — then Save Coordinates.
          </div>
        )}
        {ready && !mapLoadError && !isDevice && isDrawing && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-primary/10 border border-primary/30 text-primary rounded-lg px-3 py-1.5 text-xs shadow">
            {childDrawTargetName
              ? `Drawing polygon for "${childDrawTargetName}" — click each corner (${drawPointsCount}/${POLYGON_CORNER_COUNT}).`
              : `Click on the map to place each corner (${drawPointsCount}/${POLYGON_CORNER_COUNT}) — auto-finishes after the ${POLYGON_CORNER_COUNT}th point.`}
          </div>
        )}
        {ready && !mapLoadError && !isDevice && mainEditing && !isDrawing && !childAction && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-primary/10 border border-primary/30 text-primary rounded-lg px-3 py-1.5 text-xs shadow">
            Editing polygon for <strong>{row.name}</strong> — drag corners, then Save Changes.
          </div>
        )}
        {ready && !mapLoadError && !isDevice && !armedDeviceId && !hasPolygon && !isDrawing && !mainEditing && !childAction && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg px-3 py-1.5 text-xs text-muted-foreground shadow flex items-center gap-1.5">
            <Pentagon className="w-3.5 h-3.5" /> Draw a polygon or click a device marker for Move/Duplicate.
          </div>
        )}
        {ready && !mapLoadError && !isDevice && hasPolygon && !isDrawing && !mainEditing && !childAction && !armedDeviceId && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg px-3 py-1.5 text-xs text-muted-foreground shadow flex items-center gap-1.5">
            <MapPinned className="w-3.5 h-3.5" /> Click a device marker for options, or a child polygon to edit it.
          </div>
        )}
        {ready && !mapLoadError && !isDevice && armedDeviceId && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg px-3 py-2 text-xs shadow flex items-center gap-2 z-20">
            <span className="text-foreground">
              Ready to move <strong>{collectDirectChildDevices(row, rowsIndex).find((d) => d.id === armedDeviceId)?.name || 'device'}</strong> — drag it.
            </span>
            <button className="px-2 py-1 rounded-md border border-border text-foreground hover:bg-secondary" onClick={cancelArmedMove}>Cancel</button>
          </div>
        )}
        {ready && !mapLoadError && !isDevice && childAction && childAction.mode !== 'draw' && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg px-3 py-2 text-xs shadow flex items-center gap-2 z-20">
            {childAction.mode === 'saving' ? (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving polygon for "{childAction.child.name}"…
              </span>
            ) : (
              <>
                <span className="text-foreground">Editing polygon for <strong>{childAction.child.name}</strong></span>
                <button className="px-2 py-1 rounded-md bg-primary text-primary-foreground font-bold" onClick={saveChildEdit}>Save</button>
                <button className="px-2 py-1 rounded-md border border-border text-foreground hover:bg-secondary" onClick={cancelChildEdit}>Cancel</button>
              </>
            )}
          </div>
        )}

        {/* ────── DEVICE / POLYGON POPUP ────── */}
        {devicePopup && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setDevicePopup(null)} />
            <div
              className="absolute z-30 bg-card border border-border rounded-xl shadow-xl p-3 w-64 max-h-72 overflow-y-auto"
              style={devicePopup.pos ? { left: devicePopup.pos.x, top: devicePopup.pos.y } : { bottom: 16, left: 16 }}
            >
              {devicePopup.polygonNode ? (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-foreground truncate">{devicePopup.polygonNode.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {devicePopup.polygonNode.level} {devicePopup.polygonNode.code ? `· ${devicePopup.polygonNode.code}` : ''}
                      </p>
                    </div>
                    <button onClick={() => setDevicePopup(null)} className="text-muted-foreground hover:text-foreground flex-shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {devicePopup.polygonNode.isApiNode ? (
                      <button className="text-[10px] font-semibold px-2 py-1 rounded-md bg-secondary text-foreground hover:bg-secondary/70"
                        onClick={() => { startApiNodeEdit(devicePopup.polygonNode); setDevicePopup(null); }}>
                        Edit Polygon
                      </button>
                    ) : (
                      <button className="text-[10px] font-semibold px-2 py-1 rounded-md bg-secondary text-foreground hover:bg-secondary/70"
                        onClick={() => { startChildEdit(devicePopup.polygonNode); setDevicePopup(null); }}>
                        Edit Polygon
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs font-bold text-foreground">
                      {devicePopup.devices.length} device{devicePopup.devices.length === 1 ? '' : 's'} at this location
                    </p>
                    <button onClick={() => setDevicePopup(null)} className="text-muted-foreground hover:text-foreground flex-shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {devicePopup.devices.map((device) => (
                      <div key={device.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-secondary transition-colors">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getDeviceColor(device.deviceType) }} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-semibold text-foreground truncate">{device.name}</span>
                          <span className="block text-[10px] text-muted-foreground truncate">
                            {getDeviceTypeName(device.deviceType)}
                            {(device.code || device.raw?.device_id) ? ` · ID: ${device.code || device.raw?.device_id}` : ''}
                          </span>
                        </span>
                        <button title="Move Device" className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-background flex-shrink-0"
                          onClick={() => handleMoveDevice(device)}>
                          <Move className="w-3.5 h-3.5" />
                        </button>
                        {/* <button title="Duplicate Device" className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-background flex-shrink-0"
                          onClick={() => openDuplicateModal(device)}>
                          <Copy className="w-3.5 h-3.5" />
                        </button> */}
                        {/* <button title="Delete Device" className="p-1 rounded-md text-muted-foreground hover:text-red-500 hover:bg-background flex-shrink-0"
                          onClick={() => openDeleteModal(device)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button> */}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {duplicateModal && (
        <div className="fixed inset-0 z-[210] bg-black/50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-xl p-5 w-96 shadow-2xl">
            <p className="text-sm font-bold text-foreground mb-1">Duplicate Device</p>
            <p className="text-xs text-muted-foreground mb-4">
              Duplicating <strong>{duplicateModal.sourceDevice.name}</strong>
            </p>
            <div className="space-y-3">
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">New PND Number *</span>
                <input autoFocus
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                  value={duplicateModal.pnd_number}
                  onChange={(e) => setDuplicateModal((p) => ({ ...p, pnd_number: e.target.value }))}
                  placeholder="Enter new PND number" />
              </label>
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Type *</span>
                <input
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                  value={duplicateModal.device_type}
                  onChange={(e) => setDuplicateModal((p) => ({ ...p, device_type: e.target.value }))} />
              </label>
            </div>
            {modalError && <p className="text-[10px] text-red-500 mt-3">{modalError}</p>}
            <div className="flex flex-col gap-2 mt-4">
              <button className="w-full py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground disabled:opacity-50 flex items-center justify-center gap-1.5"
                onClick={submitDuplicate} disabled={modalSubmitting}>
                {modalSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                {modalSubmitting ? 'Duplicating...' : 'Duplicate Device'}
              </button>
              <button className="w-full py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-secondary"
                onClick={() => { setDuplicateModal(null); setModalError(null); }} disabled={modalSubmitting}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-[210] bg-black/50 flex items-center justify-center">
          {/* <div className="bg-card border border-border rounded-xl p-5 w-[420px] shadow-2xl">
            <p className="text-sm font-bold text-destructive mb-1">Delete Device</p>
            <p className="text-xs text-muted-foreground mb-4">This action cannot be undone.</p>
            <div className="py-3 px-3 border border-border rounded-lg bg-secondary/40 mb-4 space-y-2">
              <div className="flex justify-between gap-4">
                <span className="text-[11px] text-muted-foreground">Device Name</span>
                <span className="text-xs font-medium truncate max-w-[220px]">{deleteConfirm.device.name || '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[11px] text-muted-foreground">Row ID</span>
                <span className="text-xs truncate max-w-[220px]">{deleteConfirm.row_id}</span>
              </div>
            </div>
            {modalError && <p className="text-[10px] text-red-500 mb-3">{modalError}</p>}
            <div className="flex flex-col gap-2">
              <button className="w-full py-2 rounded-lg text-xs font-bold bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50 flex items-center justify-center gap-1.5"
                onClick={submitDelete} disabled={modalSubmitting}>
                {modalSubmitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Deleting...</> : <><Trash2 className="w-3.5 h-3.5" /> Delete Device</>}
              </button>
              <button className="w-full py-2 rounded-lg text-xs font-semibold border border-border hover:bg-secondary"
                onClick={() => { setDeleteConfirm(null); setModalError(null); }} disabled={modalSubmitting}>Cancel</button>
            </div>
          </div> */}
        </div>
      )}

      {addDeviceModal && (
        <div className="fixed inset-0 z-[210] bg-black/50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-xl p-5 w-96 shadow-2xl">
            <p className="text-sm font-bold text-foreground mb-1">Add Device</p>
            <p className="text-xs text-muted-foreground mb-4">Create a new PND device on this street.</p>
            <div className="space-y-3">
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">PND Number *</span>
                <input autoFocus
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                  value={addDeviceModal.pnd_number}
                  onChange={(e) => setAddDeviceModal((p) => ({ ...p, pnd_number: e.target.value }))}
                  placeholder="Enter PND number" />
              </label>
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Type *</span>
                {isAddingDeviceType ? (
                  <input
                    autoFocus
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Type new device type"
                    value={newDeviceTypeInput}
                    onChange={(e) => setNewDeviceTypeInput(e.target.value)}
                    onBlur={() => {
                      if (skipNextDeviceTypeBlurRef.current) { skipNextDeviceTypeBlurRef.current = false; return; }
                      commitNewDeviceType();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        skipNextDeviceTypeBlurRef.current = true;
                        commitNewDeviceType();
                      } else if (e.key === 'Escape') {
                        skipNextDeviceTypeBlurRef.current = true;
                        setIsAddingDeviceType(false);
                        setNewDeviceTypeInput('');
                      }
                    }}
                  />
                ) : (
                  <ReactSelect
                    placeholder="Select a device type"
                    controlHeight={32}
                    options={[
                      { value: '__ADD_NEW_DEVICE_TYPE__', label: <span className="text-[10px] font-semibold">+ Add New Type</span> },
                      ...deviceTypeOptions.map((t) => ({ value: t, label: t })),
                    ]}
                    value={
                      addDeviceModal.device_type
                        ? { value: addDeviceModal.device_type, label: addDeviceModal.device_type }
                        : null
                    }
                    onChange={(opt) => {
                      if (opt?.value === '__ADD_NEW_DEVICE_TYPE__') {
                        setIsAddingDeviceType(true);
                        setNewDeviceTypeInput('');
                        return;
                      }
                      setAddDeviceModal((p) => ({ ...p, device_type: opt?.value ?? '' }));
                    }}
                  />
                )}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Latitude</span>
                  <input className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    value={addDeviceModal.latitude}
                    onChange={(e) => setAddDeviceModal((p) => ({ ...p, latitude: e.target.value }))} />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Longitude</span>
                  <input className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    value={addDeviceModal.longitude}
                    onChange={(e) => setAddDeviceModal((p) => ({ ...p, longitude: e.target.value }))} />
                </label>
              </div>
            </div>
            {modalError && <p className="text-[10px] text-red-500 mt-3">{modalError}</p>}
            <div className="flex flex-col gap-2 mt-4">
              <button className="w-full py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground disabled:opacity-50 flex items-center justify-center gap-1.5"
                onClick={submitCreate} disabled={modalSubmitting}>
                {modalSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                {modalSubmitting ? 'Creating...' : 'Create Device'}
              </button>
              <button className="w-full py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-secondary"
                onClick={() => { setAddDeviceModal(null); setModalError(null); setIsAddingDeviceType(false); setNewDeviceTypeInput(''); }} disabled={modalSubmitting}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {confirmDiscard && (
        <div className="fixed inset-0 z-[210] bg-black/50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-xl p-5 w-80 shadow-2xl">
            <p className="text-sm font-bold text-foreground mb-1">Unsaved changes</p>
            <p className="text-xs text-muted-foreground mb-4">You have unsaved changes. Discard?</p>
            <div className="flex flex-col gap-2">
              <button className="w-full py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground"
                onClick={() => { handleSaveClick(); confirmDiscard(); setConfirmDiscard(null); }}>Save</button>
              <button className="w-full py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-secondary"
                onClick={() => { const fn = confirmDiscard; setConfirmDiscard(null); setDirty(false); fn(); }}>Discard</button>
              <button className="w-full py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-secondary"
                onClick={() => setConfirmDiscard(null)}>Continue Editing</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}